/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Picture from 'components/Picture/Picture';
import useAnimation from './animations';
import './SideImage.scss';

/**
 * This is the SideImage component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function SideImage({ layout, image, title, excerpt, author, photographer }) {
  const [ref] = useAnimation();

  function renderCopyright() {
    if (!author && !photographer) return null;

    function renderAuthor() {
      if (!author) return null;

      return <span className="author">{author}</span>;
    }

    function renderDevider() {
      if (!(author && photographer)) return null;

      return ' // ';
    }

    function renderPhotographer() {
      if (!photographer) return null;
      return <span className="photo">{photographer}</span>;
    }

    return (
      <div key="copyright" className="copyright">
        {renderAuthor()}
        {renderDevider()}
        {renderPhotographer()}
      </div>
    );
  }

  return (
    <header ref={ref} className={`side-image layout-${layout}`}>
      <div className="image">
        <div>
          <Picture {...image} />
        </div>
      </div>
      <div className="text">
        <h1 dangerouslySetInnerHTML={{ __html: title }} />
        <div key="excerpt" className="excerpt">
          <p dangerouslySetInnerHTML={{ __html: excerpt }} />
        </div>
        {renderCopyright()}
      </div>
    </header>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
SideImage.propTypes = {
  layout: PropTypes.string.isRequired,
  image: PropTypes.object,
  title: PropTypes.string,
  excerpt: PropTypes.string,
  author: PropTypes.string,
  photographer: PropTypes.string,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
SideImage.defaultProps = {
  layout: 'left',
  image: {},
};

export default SideImage;
